import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { pageKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import { useAdminGhosting } from '@/dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { updatePage as updatePageApi } from './queries/usePageQueries';

const PageContext = React.createContext();

export const PageProvider = ({ children, pageId, initialData }) => {
  const {
    data = {},
    isLoading,
    isError,
    isRefetching,
    refetch,
  } = usePageQuery(pageId, initialData);

  const { updatePage } = usePageMutation(pageId, data);

  const { setAdminGhostingSellerId } = useAdminGhosting();

  if (isError) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-2">Something went wrong</h1>

          <p className="text-gray-500">Please refresh the page and try again.</p>
        </div>
      </div>
    );
  }

  React.useEffect(() => {
    if (data?.seller) {
      setAdminGhostingSellerId(data.seller?._id || data.seller);
    }
  }, [data?.seller, setAdminGhostingSellerId]);

  return (
    <PageContext.Provider
      value={{
        page: data,
        isLoading,
        isError,
        isRefetching,
        refetch,
        updatePage,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

function usePageQuery(pageId, initialData) {
  const { token, sellerId } = useAuth();

  const { isLoading, isError, data, isRefetching, refetch } = useQuery({
    queryKey: pageKey(pageId),
    queryFn: () => fetcher(`/sellers/${sellerId}/pages/${pageId}`, token),
    enabled: !!pageId && !!token,
    initialData,
  });

  return {
    isRefetching,
    isLoading,
    isError,
    data,
    refetch,
  };
}

function usePageMutation(pageId) {
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();

  const updatePage = useMutation({
    mutationFn: values => updatePageApi(sellerId, token, values, pageId, queryClient),

    // Optimistic update
    onMutate: async values => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: pageKey(pageId) });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(pageKey(pageId));

      // Create a timestamp for this update to track the most recent update
      const updateTimestamp = Date.now();

      // Optimistically update to the new value
      queryClient.setQueryData(pageKey(pageId), old => {
        // Don't update if this is older than a previous update
        if (old?._lastUpdateTimestamp && old._lastUpdateTimestamp > updateTimestamp) {
          return old;
        }

        return {
          ...old,
          ...values,
          _lastUpdateTimestamp: updateTimestamp,
        };
      });

      // Return a context object with the previous data and timestamp
      return {
        previousData,
        updateTimestamp,
      };
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (error, values, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(pageKey(pageId), context.previousData);
      }
    },

    // Always refetch after error or success to ensure we're up to date
    onSettled: (data, error, variables, context) => {
      // Only invalidate if the request was successful or if we need fresh data
      // Don't invalidate if it would cause a flicker
      if (!error && data?.data && !data.error) {
        // Check if this is still the most recent update before invalidating
        const currentData = queryClient.getQueryData(pageKey(pageId));

        if (
          !currentData?._lastUpdateTimestamp ||
          context.updateTimestamp >= currentData._lastUpdateTimestamp
        ) {
          queryClient.invalidateQueries({ queryKey: pageKey(pageId) });
        }
      }
    },
  });

  return {
    updatePage,
  };
}

export default function usePage() {
  const context = React.useContext(PageContext);

  if (context === undefined) {
    throw new Error('usePage must be used within a PageProvider');
  }

  return context;
}
